<script setup>
import { computed } from 'vue'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Info } from 'lucide-vue-next'

const props = defineProps({
  open: { type: Boolean, default: false },
  fileName: { type: String, default: '' },
  features: { type: Array, default: () => [] }
})

defineEmits(['close'])

const INTERNAL_KEYS = new Set([
  'local_uuid', 'fileName', 'isEditing',
  '_parentUuid', '_splitIndex', '_autoCombined',
  'needsLocation'
])

const geometryBreakdown = computed(() => {
  const counts = {}
  for (const f of props.features) {
    const t = f.geometry?.type || 'None'
    counts[t] = (counts[t] || 0) + 1
  }
  return Object.entries(counts).sort((a, b) => b[1] - a[1])
})

const columnSummary = computed(() => {
  const out = new Map()
  for (const f of props.features) {
    if (!f.properties) continue
    for (const [k, v] of Object.entries(f.properties)) {
      if (INTERNAL_KEYS.has(k)) continue
      if (!out.has(k)) {
        out.set(k, { nonEmpty: 0, samples: new Set(), types: new Set() })
      }
      const slot = out.get(k)
      slot.types.add(v === null ? 'null' : typeof v)
      if (v != null && (typeof v !== 'string' || v.trim() !== '')) {
        slot.nonEmpty++
        if (slot.samples.size < 5) slot.samples.add(typeof v === 'object' ? JSON.stringify(v) : String(v))
      }
    }
  }
  return Array.from(out.entries()).map(([key, info]) => ({
    key,
    types: Array.from(info.types).join(', '),
    nonEmpty: info.nonEmpty,
    samples: Array.from(info.samples)
  })).sort((a, b) => b.nonEmpty - a.nonEmpty)
})

const firstFeatureJson = computed(() => {
  const f = props.features[0]
  if (!f) return ''
  const cleaned = { ...f, properties: {} }
  for (const [k, v] of Object.entries(f.properties || {})) {
    if (INTERNAL_KEYS.has(k)) continue
    cleaned.properties[k] = v
  }
  // Summarise coords so the dump stays readable for large MultiPolygons.
  if (cleaned.geometry?.coordinates) {
    const shape = describeShape(cleaned.geometry.coordinates)
    cleaned.geometry = { type: cleaned.geometry.type, coordinates: `<${shape}>` }
  }
  return JSON.stringify(cleaned, null, 2)
})

function describeShape(arr, depth = 0) {
  if (!Array.isArray(arr)) return 'number'
  if (arr.length === 0) return `[${depth}]0`
  return `[${arr.length} × ${describeShape(arr[0], depth + 1)}]`
}
</script>

<template>
  <Dialog :open="open" @update:open="(v) => { if (!v) $emit('close') }">
    <DialogContent class="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
      <DialogHeader>
        <DialogTitle class="flex items-center space-x-2">
          <Info class="w-5 h-5" />
          <span>Info — <span class="font-mono text-base">{{ fileName }}</span></span>
        </DialogTitle>
        <DialogDescription>
          Raw parsed data from this file. Use it to spot attributes worth mapping to
          feature names, categorizing, or importing.
        </DialogDescription>
      </DialogHeader>

      <div class="overflow-y-auto space-y-5 pr-2">
        <section>
          <h3 class="text-xs font-semibold text-muted-foreground uppercase mb-2">Summary</h3>
          <div class="flex flex-wrap gap-2 text-xs">
            <Badge variant="secondary">{{ features.length }} features</Badge>
            <Badge v-for="[type, count] in geometryBreakdown" :key="type" variant="outline">
              {{ type }}: {{ count }}
            </Badge>
          </div>
        </section>

        <section>
          <h3 class="text-xs font-semibold text-muted-foreground uppercase mb-2">
            Attribute columns ({{ columnSummary.length }})
          </h3>
          <div v-if="!columnSummary.length" class="text-sm text-muted-foreground italic">
            No attributes on these features.
          </div>
          <div v-else class="border rounded-md overflow-hidden">
            <table class="w-full text-xs">
              <thead class="bg-muted/50">
                <tr>
                  <th class="text-left px-3 py-2 font-medium">Key</th>
                  <th class="text-left px-3 py-2 font-medium">Type</th>
                  <th class="text-left px-3 py-2 font-medium">Non-empty</th>
                  <th class="text-left px-3 py-2 font-medium">Samples</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="col in columnSummary" :key="col.key" class="border-t">
                  <td class="px-3 py-1.5 font-mono">{{ col.key }}</td>
                  <td class="px-3 py-1.5 text-muted-foreground">{{ col.types }}</td>
                  <td class="px-3 py-1.5 text-muted-foreground">{{ col.nonEmpty }} / {{ features.length }}</td>
                  <td class="px-3 py-1.5 text-muted-foreground truncate max-w-xs">
                    {{ col.samples.slice(0, 3).join(' · ') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section>
          <h3 class="text-xs font-semibold text-muted-foreground uppercase mb-2">
            First feature (raw, geometry abbreviated)
          </h3>
          <pre class="text-xs bg-muted/50 p-3 rounded-md overflow-auto max-h-72 font-mono">{{ firstFeatureJson }}</pre>
        </section>
      </div>
    </DialogContent>
  </Dialog>
</template>
